<template>
  <div class="my-4 w-full mr-16 xs:!mr-0 w-full">
    <div v-if="canInvite" class="mt-2 flex justify-end">
      <button
        type="button"
        @click="openInvite"
        class="inline-flex items-center justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        <svg
          class="w-4 h-4 mr-1"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            clip-rule="evenodd"
            d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
            fill-rule="evenodd"
          />
        </svg>
        Invite user
      </button>
    </div>

    <div v-if="users.length > 0" class="mt-2 pb-2">
      <table
        class="table-auto min-w-full divide-y divide-gray-400 border-b border-gray-400 border-collapse rounded-lg"
      >
        <thead>
          <tr class="bg-gray-200 font-bold text-sm text-gray-900">
            <th scope="col" class="px-1 py-2.5 text-left">Name</th>
            <th scope="col" class="px-1 py-2.5 text-left">Email</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(user, index) in users"
            :key="index"
            class="odd:bg-white even:bg-slate-100 text-gray-900 text-sm"
          >
            <td class="px-1 py-2.5 font-bold">
              {{ user.name }}
            </td>
            <td class="px-1 py-2.5">
              {{ user.email }}
            </td>
            <td class="px-1 py-2.5">
              <index-menu-item :links="userLinks(user)" v-if="canInvite" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="users.length === 0" class="bg-white px-6 py-4 my-4">
      <strong>No users.</strong>
    </div>
  </div>

  <div v-cloak>
    <div class="modal" v-show="showInvite">
      <div class="modal-overlay" @click="closeInvite"></div>

      <div class="modal-container">
        <div class="modal-content">
          <div class="modal-heading">
            <h1>Invite user</h1>

            <div
              class="modal-close cursor-pointer z-50 block"
              @click="closeInvite"
            >
              <svg
                class="fill-current text-black"
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 18 18"
              >
                <path
                  d="M14.53 4.53l-1.06-1.06L9 7.94 4.53 3.47 3.47 4.53 7.94 9l-4.47 4.47 1.06 1.06L9 10.06l4.47 4.47 1.06-1.06L10.06 9z"
                ></path>
              </svg>
            </div>
          </div>

          <div class="modal-body">
            <p class="text-sm text-gray-600 mb-4">
              The user is emailed a link to set a password and gets access to
              this farmer's data, reports and tasks.
            </p>

            <div class="mb-3">
              <label
                class="block text-sm font-medium text-gray-700 mb-1"
                for="invite_first_name"
              >
                First name
              </label>
              <input
                class="w-full rounded border-gray-300 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-0 focus:placeholder-gray-400 sm:text-sm"
                :class="{ 'border-red-600': errorFor('first_name') }"
                id="invite_first_name"
                type="text"
                v-model="invite.first_name"
              />
              <p v-if="errorFor('first_name')" class="text-red-600 text-xs mt-1">
                {{ errorFor("first_name") }}
              </p>
            </div>

            <div class="mb-3">
              <label
                class="block text-sm font-medium text-gray-700 mb-1"
                for="invite_last_name"
              >
                Last name
              </label>
              <input
                class="w-full rounded border-gray-300 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-0 focus:placeholder-gray-400 sm:text-sm"
                :class="{ 'border-red-600': errorFor('last_name') }"
                id="invite_last_name"
                type="text"
                v-model="invite.last_name"
              />
              <p v-if="errorFor('last_name')" class="text-red-600 text-xs mt-1">
                {{ errorFor("last_name") }}
              </p>
            </div>

            <div>
              <label
                class="block text-sm font-medium text-gray-700 mb-1"
                for="invite_email"
              >
                Email
              </label>
              <input
                class="w-full rounded border-gray-300 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-0 focus:placeholder-gray-400 sm:text-sm"
                :class="{ 'border-red-600': errorFor('email') }"
                id="invite_email"
                type="email"
                v-model="invite.email"
              />
              <p v-if="errorFor('email')" class="text-red-600 text-xs mt-1">
                {{ errorFor("email") }}
              </p>
            </div>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              :class="[
                processing || !isComplete
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700',
              ]"
              :disabled="processing || !isComplete"
              @click="sendInvite"
            >
              <span v-if="processing">Sending Invitation...</span>
              <span v-else>Send Invitation</span>
            </button>

            <button
              type="button"
              class="ml-3 bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="processing"
              @click="closeInvite"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import IndexMenuItem from "@/Components/IndexMenuItem";
import { inviteFarmerUsers } from "@/Composables/useFarmerInvites";

export default {
  components: {
    IndexMenuItem,
  },
  props: {
    users: Array,
    farmer: Object,
  },

  data() {
    return {
      showInvite: false,
      processing: false,
      errors: {},
      invite: {
        first_name: "",
        last_name: "",
        email: "",
      },
    };
  },

  computed: {
    // the same rule the server applies: an administrator, or a member of the
    // organisation this farmer belongs to. Contractors are organisation members
    // too, and route model binding has already refused anyone who cannot reach
    // this farmer, so the list only has to exclude farm owners and soil samplers.
    canInvite() {
      return ["admin", "consultant", "consultant_user", "contractor"].includes(
        this.$page.props.auth.user.role
      );
    },

    isComplete() {
      return (
        this.invite.first_name.trim() &&
        this.invite.last_name.trim() &&
        this.invite.email.trim()
      );
    },
  },

  methods: {
    userLinks(user) {
      return {
        "Edit user": route("app.user.edit", user.id),
      };
    },

    openInvite() {
      this.invite = { first_name: "", last_name: "", email: "" };
      this.errors = {};
      this.showInvite = true;
    },

    closeInvite() {
      if (this.processing) {
        return;
      }

      this.showInvite = false;
    },

    // the endpoint validates a list of users, so errors come back keyed by row
    errorFor(field) {
      return this.errors["users.0." + field];
    },

    async sendInvite() {
      if (this.processing || !this.isComplete) {
        return;
      }

      this.processing = true;
      this.errors = {};

      try {
        await inviteFarmerUsers(this.farmer.id, [this.invite]);

        this.showInvite = false;

        this.$swal.fire({
          icon: "success",
          title: "Invitation Sent!",
          text: `${this.invite.first_name} ${this.invite.last_name} has been emailed an invitation.`,
          timer: 3000,
          showConfirmButton: false,
        });

        this.$inertia.reload({ only: ["users"] });
      } catch (error) {
        if (error.fieldErrors) {
          this.errors = error.fieldErrors;
        } else {
          this.$swal.fire({
            icon: "error",
            title: "Invitation Error",
            text: "The invitation could not be sent. Please try again.",
            confirmButtonText: "OK",
          });
        }
      } finally {
        this.processing = false;
      }
    },
  },
};
</script>
